import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBookmark, FiTrash2, FiBook } from 'react-icons/fi';
import 'katex/dist/katex.min.css';
import { setLocalStorageWithSync } from '../../../../utils/utils';
import { renderGreMathText as renderMathText } from '../../../../utils/renderGreMath';

const SavedConcepts = () => {
  const navigate = useNavigate();
  const [savedConcepts, setSavedConcepts] = useState([]);

  useEffect(() => {
    loadSavedConcepts();
  }, []);

  const loadSavedConcepts = () => {
    const concepts = JSON.parse(localStorage.getItem('savedConcepts')) || [];
    setSavedConcepts(concepts);
  };

  const removeConcept = async (lessonId) => {
    const updatedConcepts = savedConcepts.filter(
      (c) => c.lessonId !== lessonId
    );
    await setLocalStorageWithSync(
      'savedConcepts',
      JSON.stringify(updatedConcepts)
    );
    setSavedConcepts(updatedConcepts);
  };

  const goToLesson = (lessonId) => {
    navigate(`/exams/gre/concepts/${lessonId}`);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--liwaza-surface)',
        padding: '2rem 1rem',
      }}
    >
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() =>
              navigate('/exams/gre', { state: { activeCategory: 'concepts' } })
            }
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--liwaza-primary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
              fontSize: '1rem',
            }}
          >
            <FiArrowLeft /> Back
          </button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '0.5rem',
            }}
          >
            <div
              style={{
                background: 'var(--liwaza-primary-container)',
                padding: '1rem',
                borderRadius: '12px',
              }}
            >
              <FiBook
                size={32}
                style={{ color: 'var(--liwaza-on-primary-container)' }}
              />
            </div>
            <div>
              <h1
                style={{
                  fontSize: '2rem',
                  fontWeight: 600,
                  color: 'var(--liwaza-on-surface)',
                  margin: 0,
                }}
              >
                My Concept Book
              </h1>
              <p
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  margin: '0.25rem 0 0 0',
                }}
              >
                {savedConcepts.length}{' '}
                {savedConcepts.length === 1 ? 'concept' : 'concepts'} saved
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {savedConcepts.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              background: 'var(--liwaza-surface-container)',
              borderRadius: '12px',
            }}
          >
            <FiBookmark
              size={64}
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                marginBottom: '1rem',
              }}
            />
            <h3
              style={{
                fontSize: '1.5rem',
                color: 'var(--liwaza-on-surface)',
                marginBottom: '0.5rem',
              }}
            >
              No concepts saved yet
            </h3>
            <p style={{ color: 'var(--liwaza-on-surface-variant)' }}>
              Save quick reference concepts from lessons to access them here
              anytime!
            </p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem',
            }}
          >
            {savedConcepts.map((concept) => (
              <div
                key={concept.lessonId}
                style={{
                  background: 'var(--liwaza-surface-container-highest)',
                  borderRadius: '12px',
                  padding: '1rem',
                  border: '1px solid var(--liwaza-outline-variant)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3
                      style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'var(--liwaza-on-surface)',
                        margin: '0 0 0.25rem 0',
                        lineHeight: '1.3',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {concept.lessonTitle}
                    </h3>
                    <p
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--liwaza-on-surface-variant)',
                        margin: 0,
                      }}
                    >
                      {new Date(concept.savedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    background: 'var(--liwaza-surface-container)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    marginBottom: '0.75rem',
                    flex: 1,
                  }}
                >
                  <h4
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: 'var(--liwaza-on-surface)',
                      marginBottom: '0.5rem',
                    }}
                  >
                    Quick Reference
                  </h4>
                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      fontSize: '0.8rem',
                      color: 'var(--liwaza-on-surface-variant)',
                      lineHeight: '1.5',
                    }}
                  >
                    {concept.quickReference.map((item, index) => (
                      <li key={index} style={{ marginBottom: '0.4rem' }}>
                        • {renderMathText(item)}
                      </li>
                    ))}
                  </ul>
                </div>

                <div
                  style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}
                >
                  <button
                    onClick={() => goToLesson(concept.lessonId)}
                    style={{
                      background: 'var(--liwaza-primary-container)',
                      color: 'var(--liwaza-on-primary-container)',
                      border: 'none',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      flex: 1,
                      justifyContent: 'center',
                    }}
                  >
                    <FiBook size={12} />
                    Open the lesson
                  </button>
                  <button
                    onClick={() => removeConcept(concept.lessonId)}
                    style={{
                      background: 'var(--liwaza-error-container)',
                      color: 'var(--liwaza-on-error-container)',
                      border: 'none',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedConcepts;
